import { logger } from "./logger";
import {
  buildElectricShapeUrl,
  buildElectricWhereClause,
  isElectricConnectionConfigured,
  isValidElectricUuid,
  normalizeElectricUrl,
} from "@flowstate/core";

/**
 * Electric SQL integration for flowstate-start.
 *
 * Wraps @electric-sql/client and @electric-sql/react with helpers that
 * match the flowstate-automate API surface so both apps share conventions.
 *
 * Key exports:
 *  - ELECTRIC_URL / ELECTRIC_TABLES — config constants
 *  - getShapeUrl / getShapeStreamOptions — URL builders
 *  - isValidUuid / buildWhereClause — injection-safe WHERE helpers
 *  - testElectricConnection — runtime connectivity check
 *
 * Canonical read path: ShapeStream → TanStack DB collections → useLiveQuery.
 * See @/lib/db/collections.ts for write-through handlers.
 */

export const ELECTRIC_SHAPE_URL =
  (import.meta.env.VITE_ELECTRIC_SHAPE_URL as string | undefined) ??
  "/api/electric/shape";

/** @deprecated Use ELECTRIC_SHAPE_URL. Kept for legacy token-subdomain configs. */
export const ELECTRIC_URL =
  normalizeElectricUrl(import.meta.env.VITE_ELECTRIC_URL);

export const ELECTRIC_TABLES = {
  todos: "todos",
  profiles: "profiles",
  accounts: "accounts",
  categories_accounting: "categories_accounting",
  notifications: "notifications",
  time_blocks: "time_blocks",
  clients: "clients",
  opportunities: "opportunities",
  client_activities: "client_activities",
  task_comments: "task_comments",
  task_dependencies: "task_dependencies",
  nutrition_entries: "nutrition_entries",
  exercise_entries: "exercise_entries",
  health_metric_entries: "health_metric_entries",
  health_goals: "health_goals",
  cortex_events_log: "cortex_events_log",
  cortex_runs: "cortex_runs",
} as const;

export type ElectricTableName = keyof typeof ELECTRIC_TABLES;

/**
 * Options for configuring an Electric Shape stream subscription.
 * Compatible with @electric-sql/client ShapeStream constructor.
 */
export interface ShapeStreamOptions {
  url: string;
  where?: string;
  columns?: string[];
  signal?: AbortSignal;
}

export const isElectricConfigured = () => isElectricSyncEnabled();

export function isElectricSyncEnabled(): boolean {
  const flag = import.meta.env.VITE_ELECTRIC_ENABLED;
  if (flag === "false" || flag === "0") return false;
  if (flag === "true" || flag === "1") return true;
  return isElectricConnectionConfigured(ELECTRIC_URL);
}

// ── UUID validation (injection-safe WHERE helpers) ───────────────────────────

/**
 * Returns true when `value` is a well-formed UUID v4.
 * Use before embedding user-supplied IDs in WHERE clauses.
 */
export const isValidUuid = (value: string): boolean => isValidElectricUuid(value);

/**
 * Build a safe column equality WHERE clause.
 * Returns `undefined` when `uuid` is empty or not a valid UUID so callers
 * can easily skip the clause with `where ?? undefined`.
 */
export const buildWhereClause = (
  column: string,
  uuid: string
): string | undefined => {
  return buildElectricWhereClause(column, uuid, { quoteColumn: true });
};

export const getShapeUrl = (table: ElectricTableName, where?: string) =>
  buildElectricShapeUrl(ELECTRIC_SHAPE_URL || ELECTRIC_URL, ELECTRIC_TABLES[table], {
    where,
  });

export function getShapeUrlForTable(table: string, where?: string): string {
  const resolved =
    (ELECTRIC_TABLES as Record<string, string>)[table] ?? table;
  return buildElectricShapeUrl(ELECTRIC_SHAPE_URL || ELECTRIC_URL, resolved, {
    where,
  });
}

/**
 * Build full Shape stream options for a table, with optional where clause.
 * Ready for use with `new ShapeStream(getShapeStreamOptions("todos", { where: "..." }))`.
 */
export function getShapeStreamOptions(
  table: ElectricTableName,
  opts?: { where?: string; columns?: string[] }
): ShapeStreamOptions {
  return {
    url: getShapeUrl(table),
    ...(opts?.where ? { where: opts.where } : {}),
    ...(opts?.columns ? { columns: opts.columns } : {}),
  };
}

export const testElectricConnection = async () => {
  if (!isElectricConfigured()) {
    logger.warn("Electric SQL not configured. Set VITE_ELECTRIC_URL to enable.");
    return false;
  }

  try {
    // The proxy requires a bearer token + scope_* params; an unauthenticated
    // request legitimately returns 401 — still proves the proxy is reachable.
    const response = await fetch(getShapeUrl("todos"));
    if (response.ok || response.status === 401 || response.status === 403) {
      logger.info("Electric SQL connection successful");
      return true;
    }
    logger.error("Electric SQL connection failed", {
      status: response.status,
      statusText: response.statusText,
    });
  } catch (error) {
    logger.error("Electric SQL connection error", { error });
  }
  return false;
};
